import { CommentDetailProps } from "./commentDetail";
import { Toast as showToast } from "core/components/global/toast/toast";
import { useTranslation } from "react-i18next";
import { AuthContext } from "core/contexts/auth/auth";
import { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import { useCopyToClipboard } from "core/hooks/copytoClipboard";
import { useAppDispatch, useAppSelector } from "core/store/reduxStore";
import { IconX, IconMaximize, IconMinimize } from "@tabler/icons-react";
import { setPreviewWidth, setSelectedRow } from "modules/flywheel/store/incident";
import { v4 as uuidv4 } from "uuid";

import Markdown from 'markdown-to-jsx';
import {
  AlertSummaryContainer,
  TagsButton,
  TextContainer,
  CommentsContainer,
  AlertDetailsContainer,
  PanelContainer,
  MarkdownContainer,
  ActionButtonsContainer,
  CloseBtnContainer,
  CloseBtn,
  CloudFormation,
  AWSCLIContainer,
  TerraformContainer,
  AccordianContainer,
  ContainerHeader
} from "./alertSummary.styled";
import { 
  AccordionSummary,
  Paper, 
  Skeleton, 
  Table,
  TableBody, 
  TableCell, 
  TableContainer, 
  TableRow, 
  Typography,
  Accordion,
  AccordionDetails,
  Divider
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { UserInput } from "./alertInput";
import { ControlsDetail } from './alertControlsDetail';
import AlertComments from "./alertComments";
import CrossBtnIcon from "assets/CrossBtnIcon";
import { useTheme } from 'styled-components';
import CustomMarkdown from "core/components/global/markdown/markdown";
import ChatInterface from "./ChatInterface";
import StreamMarkdownPage from "../searchSimilar/workFlowView";
import { SimilarAlertsTableView } from "../searchSimilar/searchSimilarNew";

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

export interface IRelatedAlerts {
  id: string;
  description?: string;
  createdDate?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number | string;
  value: number | string;
}

interface AlertDetailsAccordionProps {
  assigned?: string[];
  tags?: any[];
  org?: string;
  lastModified: string;
  createdDate: string;
}

export function AlertDetailsAccordion({assigned, tags, org, lastModified, createdDate}:AlertDetailsAccordionProps) {
  return (
    <AccordianContainer>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Typography>Alert Details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper} elevation={2}>
            <Table width={690} aria-label="alert-details">
              <TableBody>
                  <TableRow
                    key={0}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">AWS Org</TableCell>
                    {
                      org ?
                      <TableCell component="th" scope="row">{org}</TableCell> : <TableCell></TableCell>
                    }
                  </TableRow>
                  <TableRow
                    key={2}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">Assigned User(s)</TableCell>
                    <TableCell component="th" scope="row">
                      {assigned?.map((name) => (
                        <span key={name}>
                          {name}
                        </span>
                      ))}
                    </TableCell>
                  </TableRow>
                  <TableRow
                    key={3}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">Last Modified</TableCell>
                    <TableCell component="th" scope="row">{lastModified}</TableCell>
                  </TableRow>
                  <TableRow
                    key={4}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">Created</TableCell>
                    <TableCell component="th" scope="row">{createdDate}</TableCell>
                  </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    </AccordianContainer>
  );
}

export interface StandardMapping {
  Object(any);
}

export interface AlertSummarySchema {
  id: string;
  severity: string;
  status: string;
  classification: string;
  organization?: string;
  assignedUsers?: string[];
  description: string;
  standardMapping: any;
  awsBestPractice: string;
  terraformRemediation: string;
  awsCliRemediation: any;
  cloudformationTemplate: string;
  createdDate: string;
  lastModified: string;
  comments: CommentDetailProps[];
  tags?: any[];
  relatedAlerts?: any[]
}

export interface AlertSummaryProps {
  setOpenSideModal: Dispatch<SetStateAction<boolean>>;
  selectedId: string;
  model_details: any;
  user_input: any;
}

export const AlertSummary = ({
  setOpenSideModal,
  selectedId,
  model_details, 
  user_input
}: AlertSummaryProps) => {
  const theme = useTheme();
  const { afUser: user } = useContext(AuthContext);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const incidentState = useAppSelector((state) => state.incidents);
  const [isLoadingGeneral, setIsLoadingGeneral] = useState(true);
  const [userPrompt, setUserPrompt] = useState("");
  const { checkAFToken } = useContext(AuthContext);
  const [tabValue, setTabValue] = useState({"actions": 0, "response": 0, "gap_analysis": ""});
  const [copiedText, copyToClipboard] = useCopyToClipboard();
  const [findingName, setFindingName] = useState("");
  const [isFullWidth, setIsFullWidth] = useState(false);
  const [prevWidth, setPrevWidth] = useState(50);
  
  // Find the selected incident from the pageEvents array
  const selectedIncident = incidentState.pageEvents.find(event => event.id === selectedId);
  
  useEffect(() => {
    // If we have the incident data, we're not loading
    if (selectedIncident) {
      setIsLoadingGeneral(false);
    }
  }, [selectedIncident]);

  useEffect(() => {
    // If we have the incident data, we're not loading
    if (user_input && user_input !== "") {
      console.log(`User Input: ${user_input}`);

      const temp: any = incidentState.pageEvents.filter((event) => {
        return event.id === incidentState.selectedRow;
      });

      console.log(`Filtered Events: ${JSON.stringify(temp)}`);
    
      if (temp.length > 0) {
        console.log(`Found matching incident: ${temp[0].id}`);
        const localFindingName = temp[0].findingName;
        // setSelectedFindingName(localFindingName);
        setFindingName(localFindingName);
        console.log(`Local Finding Name: ${localFindingName}`);
      } else {
        console.log(`No matching incident found for ID: ${incidentState.selectedRow}`);
      }
    } else {
      console.log("No user input provided or user_input is empty.");
      console.log(`Current selectedRow: ${incidentState.selectedRow}`);
    }
  }, [user_input, incidentState.selectedRow]);

  /**
   * Either point to a new tab to show or point to no tab
   * 0 will show no sections, any other number with an assoicated
   * section will show that section
   * @param newValue 
   */
  const handleTabChange = (newValue: number | string, type: string) => {
    if (type in tabValue) {
      if (newValue === tabValue[type]) {
        setTabValue((current) => ({
          ...current,
          [type]: 0
        }));    
      } else {
        setTabValue((current) => ({
          ...current,
          [type]: newValue
        }));
      }
    }
  };

  const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        style={{width: "inherit"}}
        {...other}
      >
        {value === index && (
          <PanelContainer>
            {children}
          </PanelContainer>
        )}
      </div>
    );
  };

  useEffect(() => {
    const copyText = async () => {
      if (copiedText) {
        const success = await copyToClipboard(copiedText);
        if (!success) {
          console.log("Copy failed, no permission or clipboard support, update your browser permissions.");
        }
      }
    };

    copyText();
  }, [copiedText, copyToClipboard]);
  
  const [statusColor, setStatusColor] = useState(Object());

  const statusMapping = {
    "Open": {
      "bk": theme.colors.open_incident,
      "text": theme.colors.textColor
    },
    "In Progress": {
      "bk": theme.colors.in_progress_incident,
      "text": theme.colors.textColor
    },
    "Closed": {
      "bk": theme.colors.closed_incident,
      "text": theme.colors.textColor
    },
    "Request Tuning": {
      "bk": theme.colors.requested_incident,
      "text": theme.colors.textColor
    }
  }

  // Get the severity color based on severity string
  const getSeverityColor = (severity: string | undefined) => {
    if (!severity) return "#7b7881";
    
    // This is a safer approach than using theme.severity.range[severity]
    // which was causing the TypeScript error
    switch(severity) {
      case "SEVERITY.CRITICAL":
      case "Critical":
        return theme.severity.range["SEVERITY.CRITICAL"] || "#ff0000";
      case "SEVERITY.HIGH":
      case "High":
        return theme.severity.range["SEVERITY.HIGH"] || "#ff6600";
      case "SEVERITY.MEDIUM":
      case "Medium":
        return theme.severity.range["SEVERITY.MEDIUM"] || "#ffcc00";
      case "SEVERITY.LOW":
      case "Low":
        return theme.severity.range["SEVERITY.LOW"] || "#ffff00";
      case "SEVERITY.INFORMATIVE":
      case "Informative":
        return theme.severity.range["SEVERITY.INFORMATIVE"] || "#00ff00";
      default:
        return "#7b7881";
    }
  };

  useEffect(() => {
    if (selectedIncident?.status && statusMapping[selectedIncident?.status]) {
      setStatusColor({
        text: statusMapping[selectedIncident.status].text,
        bk: statusMapping[selectedIncident.status].bk
      });
    }
  }, [selectedIncident?.status]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Generate a last modified date (if available, otherwise use current date)
  const getLastModifiedDate = () => {
    if (selectedIncident?.dateTime) {
      return formatDate(selectedIncident.dateTime);
    }
    return formatDate(new Date().toString());
  };

  // Use createdDate if available, otherwise use dateTime
  const getCreatedDate = () => {
    if (selectedIncident?.createdDate) {
      return formatDate(selectedIncident.createdDate);
    } else if (selectedIncident?.dateTime) {
      return formatDate(selectedIncident.dateTime);
    }
    return "";
  };

  // Toggle between full width and previous width
  const toggleFullWidth = ({close}: {close?: boolean} = {}) => {
    try {
      if (close) {
        // Close the side panel and reset selected row
        setOpenSideModal(false);
        dispatch(setSelectedRow({ id: "", type: "" }));
      } else {
        if (isFullWidth) {
          // Return to previous width using Redux
          if (prevWidth > 10) {
            dispatch(setPreviewWidth(prevWidth));
          } else {
            dispatch(setPreviewWidth(30));
          }
        } else {
          // Save current width before going full width
          setPrevWidth(incidentState.previewWidth);
          
          // Set to maximum width using Redux
          dispatch(setPreviewWidth(100));
        }
        setIsFullWidth(!isFullWidth);
      }
    } catch (error) {
      console.error("Error in toggleFullWidth:", error);
    }
  };

  return (
    <AlertSummaryContainer container>
      <div className="d-flex justify-content-between w-100 align-items-center mb-2">
        <div className="d-flex align-items-center">
          <strong className="text-break">{t("INCIDENTS_PAGE.INCIDENT")}: {!isLoadingGeneral && selectedIncident?.id}</strong>
          {isLoadingGeneral && <Skeleton className="ms-2" style={{width: '55%', height: 36}} animation="wave" variant="text" />}
        </div>
        <div className="d-flex">
          <button
            onClick={() => toggleFullWidth()}
            style={{ color: theme.colors.textColor}}
            className={`p-2 rounded-full transition-colors`}
            title="Toggle width"
          >
            {isFullWidth ? (
              <IconMinimize size={18} stroke={1.5} />
            ) : (
              <IconMaximize size={18} stroke={1.5} />
            )}
          </button>
          <button
            onClick={() => toggleFullWidth({close: true})}
            style={{ color: theme.colors.textColor}}
            className={`p-2 rounded-full transition-colors`}
            title="Close"
          >
            <IconX size={18} stroke={1.5} />
          </button>
        </div>
      </div>
    
      <AlertDetailsAccordion 
        assigned={selectedIncident?.assignedto ? [selectedIncident.assignedto] : []} 
        org={selectedIncident?.organization || selectedIncident?.org || ""}
        lastModified={getLastModifiedDate()}
        createdDate={getCreatedDate()}
      />

      { isLoadingGeneral ? <>
          <div className="d-flex flex-row">
              <Skeleton style={{width: 150, height: 52}} animation="wave" variant="text" />
              <Skeleton className="ms-2" style={{width: 150, height: 52}} animation="wave" variant="text" />
          </div>
          </> : <>
          <div className="d-flex flex-row">
            { selectedIncident ?
              <>
                <div style={{backgroundColor: statusColor?.bk || "#7b7881"}} className="mt-2 mb-2 ps-4 pe-4 pt-2 pb-2 rounded-3 shadow">
                  <strong style={{color: "rgb(39, 55, 70)", opacity: 0.9}}>{selectedIncident.status}</strong>
                </div>
                <div style={{backgroundColor: getSeverityColor(selectedIncident.severity)}} className="m-2 ms-2 ps-4 pe-4 pt-2 pb-2 rounded-3 shadow">
                  <strong style={{color: "rgb(39, 55, 70)", opacity: 0.9}}>{t("INCIDENTS_PAGE.SEVERITY")}: {selectedIncident.severity}</strong>
                </div>
              </> :
              <>
                <div style={{backgroundColor: "#7b7881"}} className="mt-2 mb-2 ps-4 pe-4 pt-2 pb-2 rounded-3 shadow">
                  <strong style={{color: "rgb(39, 55, 70)", opacity: 0.9}}>Status</strong>
                </div>
                <div style={{backgroundColor: "#7b7881"}} className="m-2 ms-2 ps-4 pe-4 pt-2 pb-2 rounded-3 shadow">
                  <strong style={{color: "rgb(39, 55, 70)", opacity: 0.9}}>{t("INCIDENTS_PAGE.SEVERITY")}</strong>
                </div>
              </>
            }
          </div>
        </>
      }
      
      { selectedIncident?.findingName ?
        <TextContainer container spacing={2} className="d-flex flex-column">
          <strong className="mb-3" style={{fontSize: theme.font.large}}>Finding Name:</strong>
          <strong>{selectedIncident.findingName || "Multiple Failed Login Attempts from Unusual Location"}</strong>
          
          {selectedIncident.findingId && (
            <div style={{ fontSize: '0.9rem', marginTop: '10px' }}>
              <strong>Finding ID:</strong> {selectedIncident.findingId}
            </div>
          )}
        </TextContainer> :
        isLoadingGeneral && 
          <>
            <Skeleton style={{width: 150, height: 52}} animation="wave" variant="text" />
            <Skeleton className="w-100" style={{height: 150}} animation="wave" variant="text" />
          </>
      }
      
      {/* AI Workflow section*/}
      {(model_details.type=='workflow' && findingName !== "") &&
        <div style={{ overflow: 'auto', wordWrap: 'break-word', maxWidth: '100%' }}>
          <StreamMarkdownPage user_input={findingName} conversationId={selectedId}/>
        </div>
      }

      {/* Comments Section */}
      <CommentsContainer className="mb-2">
        <AlertComments 
          t={t} theme={theme} 
          initialComments={[]} 
          alertId={selectedIncident?.id || ""} 
          user={user} 
        />
      </CommentsContainer>

      {/* Conversation Section */}
      <strong style={{fontSize: theme.font.xl}}>{t("INCIDENTS_PAGE.CONVERSE_WITH_AI_FLYWHEEL")}:</strong>
      
      <ChatInterface 
        userPrompt={userPrompt}
        setUserPrompt={setUserPrompt}
        theme={theme}
        conversationId={selectedId}
      />

      <TextContainer container spacing={2} className="d-flex flex-column">
        <strong style={{fontSize: theme.font.xl}}>{t("INCIDENTS_PAGE.AI_DISCLAMER_TITLE")}:</strong>
        <strong style={{fontSize: theme.font.large}}>{t("INCIDENTS_PAGE.PLEASE_NOTE")}:</strong> 
        <strong className="mt-2">The information provided here has been generated by AI and, as such, may contain inaccuracies or &#39;hallucinations&#39;. We advise users to verify the accuracy of the information before relying on it for any critical decisions or actions.</strong>
      </TextContainer>

    </AlertSummaryContainer>
  );
};

export default AlertSummary;